import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Sparkles, Loader2, CheckCircle2, XCircle, ArrowRight, Sprout, Clock } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import PayPalButton from "@/components/PayPalButton";
import {
    fetchBillingStatus, fetchCheckoutStatus, fetchBillingHistory, formatApiError,
} from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const POLL_INTERVAL = 2500;
const POLL_MAX_ATTEMPTS = 20;

function daysUntil(iso) {
    if (!iso) return 0;
    const diff = new Date(iso).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

export default function Billing() {
    const { refresh } = useAuth();
    const [params, setParams] = useSearchParams();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState(null);
    const [history, setHistory] = useState([]);
    const [checkoutState, setCheckoutState] = useState(sessionId ? "pending" : "idle");
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const [s, h] = await Promise.all([fetchBillingStatus(), fetchBillingHistory()]);
            setStatus(s);
            setHistory(h.transactions || []);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    useEffect(() => { load(); }, []);

    // Poll for checkout completion when we return from Stripe
    useEffect(() => {
        if (!sessionId) return;
        let attempts = 0;
        let cancelled = false;

        const poll = async () => {
            if (cancelled) return;
            attempts += 1;
            try {
                const res = await fetchCheckoutStatus(sessionId);
                if (res.payment_status === "paid" && res.applied) {
                    setCheckoutState("success");
                    await refresh();
                    await load();
                    // Remove ?session_id from URL
                    setParams({}, { replace: true });
                    toast.success("Welcome to Plus! 🌿");
                    return;
                }
                if (res.status === "expired" || res.payment_status === "failed") {
                    setCheckoutState("failed");
                    return;
                }
            } catch (e) {
                // Transient Stripe polling errors are expected (rate limits,
                // 404 while the session settles). We keep polling; only log
                // in dev so devtools surfaces persistent issues.
                if (process.env.NODE_ENV !== "production") {
                    console.warn("Stripe poll attempt failed:", e?.message || e);
                }
            }
            if (attempts < POLL_MAX_ATTEMPTS) {
                setTimeout(poll, POLL_INTERVAL);
            } else {
                setCheckoutState("timeout");
            }
        };
        poll();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId]);

    const upgrade = async () => {
        // Stripe is currently disabled — users should be using the
        // inline PayPal button below this button instead. This is a
        // belt-and-braces guard for anyone hitting a cached page.
        toast.info("Use the PayPal button to extend your Plus pass.");
    };

    const onPayPalSuccess = async () => {
        await Promise.all([refresh(), load()]);
        toast.success("Plus extended by 30 days 🌿");
    };

    const isPlus = status?.tier === "plus";
    const daysLeft = useMemo(() => daysUntil(status?.plus_until), [status?.plus_until]);

    return (
        <div className="space-y-10" data-testid="billing-page">
            <PageHeader
                overline="Your plan"
                title="Billing"
                description="Manage your rooteed Plus pass, see renewal date and purchase history."
            />

            {checkoutState === "pending" && (
                <div className="journal-card p-6 flex items-center gap-3" data-testid="checkout-polling">
                    <Loader2 className="w-5 h-5 animate-spin text-[var(--vm-accent)]" />
                    <span className="text-[var(--vm-text-secondary)]">
                        Confirming your payment…
                    </span>
                </div>
            )}
            {checkoutState === "success" && (
                <div className="journal-card p-6 border-2 border-[var(--vm-sage)] flex items-center gap-3" data-testid="checkout-success">
                    <CheckCircle2 className="w-6 h-6 text-[var(--vm-sage)]" />
                    <span className="text-[var(--vm-text)]">
                        You&apos;re now on rooteed Plus 🌿 — enjoy your unlimited garden!
                    </span>
                </div>
            )}
            {(checkoutState === "failed" || checkoutState === "timeout") && (
                <div className="journal-card p-6 border-2 border-[var(--vm-secondary)] flex items-center gap-3" data-testid="checkout-failed">
                    <XCircle className="w-6 h-6 text-[var(--vm-secondary)]" />
                    <span className="text-[var(--vm-text)]">
                        We couldn&apos;t confirm your payment. If the charge went through, it&apos;ll
                        sync shortly — refresh in a minute.
                    </span>
                </div>
            )}

            {/* Current plan card */}
            <section className="journal-card p-8" data-testid="current-plan-card">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <div className="overline text-[var(--vm-sage)] mb-1">Current plan</div>
                        <h2 className="font-serif-display text-4xl text-[var(--vm-text)]">
                            {isPlus ? "rooteed Plus 🌿" : "Free Seedling"}
                        </h2>
                    </div>
                    {isPlus ? (
                        <Sparkles className="w-10 h-10 text-[var(--vm-accent)]" strokeWidth={1.5} />
                    ) : (
                        <Sprout className="w-10 h-10 text-[var(--vm-sage)]" strokeWidth={1.5} />
                    )}
                </div>

                {isPlus ? (
                    <div className="space-y-2">
                        <div className="flex items-center gap-2 text-[var(--vm-text-secondary)]">
                            <Clock className="w-4 h-4" />
                            <span data-testid="plus-expiry">
                                {daysLeft} day{daysLeft === 1 ? "" : "s"} left ·
                                renews <strong className="text-[var(--vm-text)]">
                                    {new Date(status.plus_until).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                                </strong> unless extended
                            </span>
                        </div>
                        <div className="mt-4 max-w-sm" data-testid="extend-plus-paypal-wrapper">
                            <div className="text-xs text-[var(--vm-text-muted)] mb-2">
                                Extend by 30 more days — €2 · pay with PayPal or any card
                            </div>
                            <PayPalButton packageId="plus_monthly" onSuccess={onPayPalSuccess} disabled={busy} />
                        </div>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <p className="text-[var(--vm-text-secondary)] text-sm">
                            You&apos;re on the free plan. Ready for unlimited gardens, crops, chats
                            and 100 photo uploads?
                        </p>
                        <div className="max-w-sm" data-testid="billing-upgrade-paypal-wrapper">
                            <PayPalButton packageId="plus_monthly" onSuccess={onPayPalSuccess} disabled={busy} />
                        </div>
                        <Button asChild variant="ghost" size="sm">
                            <Link to="/pricing">Compare plans <ArrowRight className="w-3 h-3 ml-1" /></Link>
                        </Button>
                    </div>
                )}
            </section>

            {/* Usage snapshot */}
            {status && (
                <section className="journal-card p-8" data-testid="usage-card">
                    <div className="overline text-[var(--vm-sage)] mb-3">Current usage</div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                        {[
                            ["Gardens", status.usage?.gardens, status.limits?.gardens],
                            ["Crops", status.usage?.crops, status.limits?.crops],
                            ["Images", status.usage?.images, status.limits?.images],
                        ].map(([label, used, cap]) => (
                            <div key={label}>
                                <div className="text-xs text-[var(--vm-text-muted)] uppercase tracking-wider mb-1">{label}</div>
                                <div className="font-serif-display text-3xl text-[var(--vm-text)]">
                                    {used ?? 0}{cap != null && <span className="text-[var(--vm-text-muted)] text-lg"> / {cap}</span>}
                                    {cap == null && <span className="text-[var(--vm-sage)] text-base"> · unlimited</span>}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Transaction history */}
            <section>
                <div className="overline text-[var(--vm-sage)] mb-3">Purchase history</div>
                {history.length === 0 ? (
                    <div className="journal-card p-6 text-sm text-[var(--vm-text-muted)] italic">
                        No purchases yet.
                    </div>
                ) : (
                    <div className="journal-card overflow-hidden">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-[var(--vm-border)] text-xs uppercase tracking-wider text-[var(--vm-text-muted)]">
                                    <th className="text-left px-4 py-3">Date</th>
                                    <th className="text-left px-4 py-3">Package</th>
                                    <th className="text-right px-4 py-3">Amount</th>
                                    <th className="text-center px-4 py-3">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map((tx) => (
                                    <tr key={tx.session_id} data-testid={`tx-${tx.session_id}`} className="border-b border-[var(--vm-border)]/50">
                                        <td className="px-4 py-3 text-[var(--vm-text-secondary)]">
                                            {new Date(tx.created_at).toLocaleDateString()}
                                        </td>
                                        <td className="px-4 py-3 text-[var(--vm-text)]">{tx.package_id}</td>
                                        <td className="px-4 py-3 text-right">{tx.amount} {(tx.currency || "").toUpperCase()}</td>
                                        <td className="px-4 py-3 text-center">
                                            <span
                                                className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${
                                                    tx.payment_status === "paid"
                                                        ? "bg-[var(--vm-sage)]/20 text-[var(--vm-sage)]"
                                                        : tx.payment_status === "pending"
                                                            ? "bg-[var(--vm-accent)]/15 text-[var(--vm-accent)]"
                                                            : "bg-[var(--vm-secondary)]/15 text-[var(--vm-secondary)]"
                                                }`}
                                            >
                                                {tx.payment_status}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
}
